"use client";

import { MenuIcon } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";

import { OrganizationAvatar } from "@product/components/app/organization/organization-avatar";
import { AppMenubarView } from "@product/components/app/shell/app-menubar-view";
import { NewSessionSharing } from "@product/components/app/sessions/new-session-sharing";
import { ComposerSourcesView } from "@product/components/app/sessions/composer-sources-view";
import { EntityMentionInput } from "@product/components/app/sessions/entity-mention-input";
import { PromptDictationControl } from "@product/components/app/sessions/prompt-dictation-control";
import {
  PromptModelTriggerView,
  promptModelTriggerLabel,
} from "@product/components/app/sessions/prompt-model-trigger";
import { ComposerAttachButton } from "@product/components/app/sessions/session-start-composer-view";
import {
  SESSION_COMPOSER_BUSY_PLACEHOLDER,
  SESSION_COMPOSER_PLACEHOLDER,
  SessionComposerForm,
} from "@product/app/workspace/sessions/[sessionId]/session-screen-view";
import {
  ChannelNavigationList,
  NavigationCreateAction,
  SessionNavigationList,
} from "@product/components/app/workspace/workspace-navigation-lists";
import {
  mergePinnedChannels,
  mergePinnedSessions,
} from "@product/components/app/workspace/workspace-navigation-model";
import { WorkspaceNavigationView } from "@product/components/app/workspace/workspace-navigation-view";
import { Button } from "@product/components/ui/button";
import { Menubar, MenubarMenu, MenubarTrigger } from "@product/components/ui/menubar";
import type { ComposerSource } from "@product/components/app/sessions/composer-sources-view";
import type { WorkspaceSession } from "@product/lib/mlx-workspace-client";
import { cn } from "@product/lib/utils";

import { OSTREY_WORKSPACE, type ShotWorkspace } from "./workspace";

/** Every control in a shot is either inert or intercepted by the shot. */
const noop = () => {};
const never = () => false;

/* ------------------------------------------------------------------ */
/*  Menu bar                                                            */
/* ------------------------------------------------------------------ */

/** The product's default letter tile: orange with a white initial. */
const ORGANISATION_ICON = {
  style: "letter",
  backgroundColor: "#f97316",
  textColor: "#ffffff",
  letterScale: 0.75,
} as const;

/**
 * AppPhoneMenu's trigger, as the menu bar shows it below 640px: its menu
 * stays closed in a shot, so the button is the whole of it.
 */
function ShotPhoneMenuTrigger() {
  return (
    <Button variant="ghost" size="sm" className="h-7 px-1.5 text-sm font-semibold sm:hidden" aria-label="Menu" tabIndex={-1}>
      <MenuIcon data-icon="inline-start" />
      Menu
    </Button>
  );
}

/**
 * The application menu bar with the shot's organisation and user. The bar is
 * the product's AppMenubarView; the organisation and account triggers are
 * composed from the same primitives and classes the header menus use
 * (admin-control-header-menus.tsx), whose menus stay closed in a shot.
 * `inboxBadge` is the Inbox link's count, when the shot has Inbox items that
 * need attention (InboxAttentionBadgeView).
 */
export function ShotMenubar({
  workspace = OSTREY_WORKSPACE,
  inboxBadge,
}: { workspace?: ShotWorkspace; inboxBadge?: ReactNode } = {}) {
  return (
    <AppMenubarView
      organizationControl={
        <div className="flex min-w-0 items-center gap-2">
          <Menubar className="bg-transparent p-0">
            <MenubarMenu>
              <MenubarTrigger
                className="justify-center px-1"
                aria-label={`Organisation: ${workspace.organisationName}`}
                title={workspace.organisationName}
              >
                <OrganizationAvatar
                  name={workspace.organisationName}
                  icon={ORGANISATION_ICON}
                  size={18}
                  className="rounded-[4px]"
                />
              </MenubarTrigger>
            </MenubarMenu>
          </Menubar>
        </div>
      }
      userControl={
        <Menubar className="bg-transparent p-0">
          <MenubarMenu>
            <MenubarTrigger
              className={cn(
                "size-[22px] justify-center overflow-hidden rounded-full px-0 py-0 text-2xs",
                "bg-primary text-primary-foreground hover:bg-primary/90 data-open:bg-primary/90",
              )}
              aria-label="Account"
              title="Account"
            >
              {workspace.user.label[0]}
            </MenubarTrigger>
          </MenubarMenu>
        </Menubar>
      }
      twinOpen={false}
      syncDot={{
        label: "Everything is up to date",
        dotClassName: "bg-badge-green ring-badge-green/20",
      }}
      onOpenTwin={noop}
      phoneMenu={<ShotPhoneMenuTrigger />}
      newSessionHref="/workspace/sessions/new"
      onOpenNewSession={noop}
      recentSessions={workspace.sessions.slice(0, 5)}
      showFeed
      showInbox
      inboxBadge={inboxBadge}
      showWorkflows
      canManageOrganization={false}
      shortcutsLabel="Cmd+/"
      onStartTour={noop}
      onOpenShortcuts={noop}
      onSendFeedback={noop}
    />
  );
}

/* ------------------------------------------------------------------ */
/*  Workspace rail                                                      */
/* ------------------------------------------------------------------ */

/** Unread sessions, less the one open in the pane (opening one reads it). */
function readStates(workspace: ShotWorkspace, activeId: string) {
  return new Map(
    workspace.unreadSessionIds.filter((id) => id !== activeId).map((id) => [id, { unread: true }]),
  );
}
/** The owner every rail row names: the person signed in. */
function owner(workspace: ShotWorkspace) {
  return {
    label: workspace.user.label,
    title: workspace.user.label,
    ownerId: workspace.ownerId,
    imageUrl: null,
    initials: workspace.user.initials,
  };
}
/** No search is running in a shot. */
const IDLE_SEARCH = {
  results: [],
  interpretation: undefined,
  error: null,
  loadMoreError: null,
  isLoading: false,
  isLoadingMore: false,
  hasMore: false,
  loadMore: async () => {},
} as unknown as ComponentProps<typeof SessionNavigationList>["search"];

/** The workspace rail on its Sessions or Channels tab. */
export function ShotRail({
  workspace = OSTREY_WORKSPACE,
  tab = "sessions",
  activeId = workspace.activeSessionId,
  activeChannelId = null,
  sessions = workspace.sessions,
}: {
  workspace?: ShotWorkspace;
  tab?: "sessions" | "channels";
  /** The session open in the pane. */
  activeId?: string;
  /** The channel open in the pane. */
  activeChannelId?: string | null;
  /** The session list, e.g. with the open session running. */
  sessions?: WorkspaceSession[];
} = {}) {
  const rowOwner = owner(workspace);
  return (
    <WorkspaceNavigationView
      tab={tab}
      onTabChange={noop}
      query=""
      onQueryChange={noop}
      sessionScope="everyone"
      onSessionScopeChange={noop}
      onCollapse={noop}
      sessionsList={
        <SessionNavigationList
          activeId={activeId}
          groups={mergePinnedSessions(sessions, workspace.pinnedSessions)}
          hiddenIds={new Set()}
          limit={50}
          pendingId={null}
          pinPending={never}
          readPending={never}
          readStates={readStates(workspace, activeId)}
          pinnedIds={new Set(workspace.pinnedSessions.map((session) => session.id))}
          query=""
          search={IDLE_SEARCH}
          sessions={sessions}
          ownerForSession={() => rowOwner}
          createAction={
            <NavigationCreateAction
              newSessionHref="/workspace/sessions/new"
              onNewSession={noop}
              tab="sessions"
            />
          }
          onAction={noop}
          onArchive={noop}
          onDelete={noop}
          onPin={noop}
          onReadChange={noop}
          onRename={noop}
          onShowMore={noop}
        />
      }
      channelsList={
        <ChannelNavigationList
          activeId={activeChannelId}
          groups={mergePinnedChannels(workspace.channels, [])}
          limit={50}
          pendingId={null}
          pinPending={never}
          pinnedIds={new Set()}
          query=""
          runningIds={new Set()}
          ownerForChannel={() => rowOwner}
          createAction={<NavigationCreateAction onNewChannel={noop} tab="channels" />}
          onArchive={noop}
          onPin={noop}
          onRename={noop}
          onShowMore={noop}
        />
      }
    />
  );
}

/* ------------------------------------------------------------------ */
/*  Composer                                                            */
/* ------------------------------------------------------------------ */

/**
 * The session's follow-up composer: the product's composer form with its
 * real text field, attach, voice, model, sources and privacy controls, and
 * send, or stop while the run works. The voice control is the markup alone:
 * it records nothing and binds no shortcut on the marketing page.
 *
 * The text field is the EntityMentionInput that the app's
 * PromptChannelMentionInput renders. That wrapper also reads the workspace's
 * files through its data client for "@" mentions; with no channels, views
 * or files to offer, it renders exactly this field, and the mention list
 * never opens on a read-only field.
 */
export function ShotComposer({
  prompt = "",
  busy = false,
  sending = false,
  sources = OSTREY_WORKSPACE.composerSources,
}: {
  prompt?: string;
  /** A run is working: the product offers stop and locks model and voice. */
  busy?: boolean;
  sending?: boolean;
  /** The workspace's connected sources, for the sources row. */
  sources?: readonly ComposerSource[];
}) {
  return (
    <SessionComposerForm
      onSubmit={(event) => event.preventDefault()}
      textarea={
        <EntityMentionInput
          value={prompt}
          onValueChange={noop}
          entities={[]}
          listboxLabel="Mention a channel, Data Set, view, or file"
          onMention={noop}
          placeholder={busy ? SESSION_COMPOSER_BUSY_PLACEHOLDER : SESSION_COMPOSER_PLACEHOLDER}
          rows={4}
          readOnly
          className="mlx-prompt-textarea"
        />
      }
      attach={<ComposerAttachButton disabled={false} uploading={false} />}
      model={
        <PromptModelTriggerView
          explicit={false}
          disabled={busy || sending}
          label={promptModelTriggerLabel({
            venus: false,
            modelLabel: "Default",
            reasoningLabel: "Off",
          })}
          modelId="default"
          reasoning="off"
        />
      }
      sources={<ComposerSourcesView sources={sources} offerConnect={false} />}
      privacy={
        <NewSessionSharing
          channelId={null}
          shared={false}
          disabled={false}
          onChange={noop}
        />
      }
      dictation={
        <PromptDictationControl state="idle" elapsed={0} blocked={busy || sending} />
      }
      busy={busy}
      stopping={false}
      sendDisabled={!prompt.trim() || sending}
      sending={sending}
    />
  );
}
